/**
 * Game-rules reference sheet shown via a "?" button in both the lobby and
 * the game view. Each game has its own content block; common shell handles
 * the overlay, header, and close affordance.
 */

import { useState } from "react";
import type { GameKind } from "../../../shared/protocol.js";
import { GAME_LABELS } from "../lib/gameKinds.js";
import { PlayingCard } from "./PlayingCard.js";

/** Floating "?" button. Position with a CSS class on the parent if needed. */
export function RulesButton({
  gameKind,
  className,
}: {
  gameKind: GameKind;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className={`rules-button ${className ?? ""}`}
        onClick={() => setOpen(true)}
        title={`${GAME_LABELS[gameKind]} — rules`}
        aria-label="Game rules"
      >
        ?
      </button>
      {open && <RulesSheet gameKind={gameKind} onClose={() => setOpen(false)} />}
    </>
  );
}

function RulesSheet({ gameKind, onClose }: { gameKind: GameKind; onClose: () => void }) {
  return (
    <div className="rules-overlay" onClick={onClose}>
      <div
        className="rules-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`${GAME_LABELS[gameKind]} rules`}
      >
        <div className="rules-header">
          <h2 style={{ margin: 0 }}>{GAME_LABELS[gameKind]} — Rules</h2>
          <button onClick={onClose}>Close</button>
        </div>
        <div className="rules-content">
          {gameKind === "hearts" && <HeartsRules />}
          {gameKind === "shengji" && <ShengJiRules />}
          {gameKind === "cabo" && <CaboRules />}
          {gameKind === "ohhell" && <OhHellRules />}
          {gameKind === "euchre" && <EuchreRules />}
          {gameKind === "regicide" && <RegicideRules />}
          {gameKind === "swoop" && <SwoopRules />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helpers for rules content
// ---------------------------------------------------------------------------

const RULES_CARD_W = 52;

function C({ id }: { id: string }) {
  return (
    <span className="rules-inline-card">
      <PlayingCard card={id} width={RULES_CARD_W} />
    </span>
  );
}

function CardRow({ ids }: { ids: string[] }) {
  return (
    <div className="rules-card-row">
      {ids.map((id, i) => (
        <PlayingCard key={i} card={id} width={RULES_CARD_W} />
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rules-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

// ===========================================================================
// Hearts
// ===========================================================================

function HeartsRules() {
  return (
    <>
      <Section title="Goal">
        <p>
          Avoid taking <strong>point cards</strong>. The game ends when any player
          reaches <strong>100 points</strong>; the player with the
          <strong> lowest cumulative score</strong> wins.
        </p>
      </Section>

      <Section title="Setup">
        <p>
          2–8 players. The deck is dealt evenly between players (extras are
          discarded). The 2 of Clubs leads the first trick.
        </p>
        <CardRow ids={["2C"]} />
      </Section>

      <Section title="Point cards">
        <table className="rules-table">
          <thead>
            <tr><th>Card</th><th>Points</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><C id="2H" /> <C id="5H" /> <C id="TH" /> (any ♥)</td>
              <td>1 point each</td>
            </tr>
            <tr>
              <td><C id="QS" /> (Queen of Spades)</td>
              <td>13 points</td>
            </tr>
            <tr>
              <td>Anything else</td>
              <td>0 points</td>
            </tr>
          </tbody>
        </table>
        <p className="rules-muted">There are 26 point cards in total per round.</p>
      </Section>

      <Section title="Playing a trick">
        <ol>
          <li>Each player plays one card in clockwise order, starting with the lead.</li>
          <li>You <strong>must follow the led suit</strong> if you can.</li>
          <li>If you can't, play any card (this is your chance to dump points).</li>
          <li>The highest card <em>of the led suit</em> wins the trick and leads the next one.</li>
        </ol>
        <p className="rules-muted">
          Off-suit cards never win the trick, no matter how high — only cards of
          the led suit are in the running.
        </p>
      </Section>

      <Section title="Hearts must be broken">
        <p>
          You can't <strong>lead</strong> a heart until hearts have been
          "broken" — i.e., someone has played a heart as a discard on an
          off-suit trick. Until then, lead any other suit.
        </p>
        <p className="rules-muted">
          Exception: if your hand contains only hearts, you may lead one.
        </p>
      </Section>

      <Section title="Shoot the moon">
        <p>
          If a single player takes <strong>every</strong> point card in a
          round — all 13 hearts <em>and</em> the <C id="QS" /> — they shoot
          the moon: they score 0 that round and every other player gets +26.
        </p>
      </Section>

      <Section title="End of round / end of game">
        <p>
          Each round ends when all cards are played. Points are added to
          everyone's cumulative score. The game ends as soon as any player
          reaches 100 points; the player with the <strong>lowest</strong>{" "}
          cumulative score wins.
        </p>
      </Section>
    </>
  );
}

// ===========================================================================
// Sheng Ji
// ===========================================================================

function ShengJiRules() {
  return (
    <>
      <Section title="Goal">
        <p>
          A team game played as a series of rounds. Each team has a current
          trump rank starting at 2; win rounds to push your rank up to Ace.{" "}
          <strong>First team to clear Ace wins the match.</strong>
        </p>
      </Section>

      <Section title="Setup">
        <table className="rules-table">
          <thead>
            <tr><th>Players</th><th>Decks</th><th>Hand size</th><th>Kitty</th></tr>
          </thead>
          <tbody>
            <tr><td>4</td><td>2 (108 cards)</td><td>25</td><td>8</td></tr>
            <tr><td>5</td><td>2 (108 cards)</td><td>20</td><td>8</td></tr>
            <tr><td>6</td><td>3 (162 cards)</td><td>25</td><td>12</td></tr>
          </tbody>
        </table>
        <p className="rules-muted">
          Jokers are in play. The <strong>kitty</strong> is a face-down pile
          dealt last that goes to the round's declarer.
        </p>
      </Section>

      <Section title="Trump rank &amp; suit">
        <p>
          Each round has a <strong>trump rank</strong> (the current rank of
          the defending team) and a <strong>trump suit</strong> (determined
          by declarations during dealing). Trump cards beat all non-trump
          cards, regardless of suit.
        </p>
        <p>
          The trump <em>ladder</em>, from lowest to highest, looks like:
        </p>
        <ol className="rules-muted" style={{ fontSize: 13 }}>
          <li>Trump-suit cards below the trump rank, by their natural rank order.</li>
          <li>Trump-suit cards above the trump rank, by their natural rank order.</li>
          <li>The <em>trump rank</em> in non-trump suits (any one of these beats every card above).</li>
          <li>The <em>trump rank</em> in the trump suit (beats all of the above).</li>
          <li>Small Joker <C id="SJ" /></li>
          <li>Big Joker <C id="BJ" /></li>
        </ol>
      </Section>

      <Section title="Phases">
        <ol>
          <li>
            <strong>Dealing</strong> — cards are dealt one at a time. During
            dealing, any player can declare a trump suit (see below).
          </li>
          <li>
            <strong>Kitty bury</strong> — the declarer takes the kitty into
            their hand, then buries an equal number of cards face-down.
            Those cards don't see play but count for the attackers at round
            end if the attackers win the last trick.
          </li>
          <li>
            <strong>Call friends</strong> (Find-the-Friend mode only) — the
            declarer secretly names cards; whoever plays them becomes a
            teammate.
          </li>
          <li>
            <strong>Play tricks</strong> — the declarer leads; play
            continues until all cards are exhausted.
          </li>
          <li>
            <strong>Round end</strong> — points are tallied; ranks advance.
          </li>
        </ol>
      </Section>

      <Section title="Declarations (during dealing)">
        <p>Each declaration must be <em>stronger</em> than the previous one.</p>
        <table className="rules-table">
          <thead>
            <tr><th>Strength</th><th>Cards</th><th>Sets trump suit to</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>1 — Single trump rank</td>
              <td><C id="2H" /> (the trump rank in any suit)</td>
              <td>That card's suit</td>
            </tr>
            <tr>
              <td>2 — Pair of trump rank</td>
              <td><C id="2H" /> <C id="2H" /></td>
              <td>That suit</td>
            </tr>
            <tr>
              <td>3 — Small joker pair</td>
              <td><C id="SJ" /> <C id="SJ" /></td>
              <td>No-trump (only jokers + rank cards are trump)</td>
            </tr>
            <tr>
              <td>4 — Big joker pair</td>
              <td><C id="BJ" /> <C id="BJ" /></td>
              <td>No-trump</td>
            </tr>
          </tbody>
        </table>
        <p className="rules-muted">
          If no one declares, the dealer becomes the declarer and the trump
          suit is taken from the top non-joker card of the kitty.
        </p>
      </Section>

      <Section title="Point cards">
        <table className="rules-table">
          <thead><tr><th>Card</th><th>Points</th></tr></thead>
          <tbody>
            <tr><td><C id="5H" /> <C id="5S" /> (any 5)</td><td>5 pts</td></tr>
            <tr><td><C id="TC" /> <C id="KD" /> (any 10 or K)</td><td>10 pts</td></tr>
            <tr><td>Everything else</td><td>0 pts</td></tr>
          </tbody>
        </table>
        <p className="rules-muted">
          Each deck contains 200 points (40×5 + 8×10 + 8×10 = 200). Defenders
          win the round if the attackers' captured points are <strong>under 80</strong>.
        </p>
      </Section>

      <Section title="Leading and following">
        <p>
          The trick leader plays any single, pair, or tractor (consecutive
          pairs). Each other player must follow with the same number of
          cards, matching the lead's <em>effective suit</em> if they can.
        </p>
        <CardRow ids={["7H", "7H", "8H", "8H"]} />
        <p className="rules-muted">
          Above: a "tractor" — two consecutive pairs in the same suit. If
          you have a matching tractor in the same suit, you must play it.
        </p>
      </Section>

      <Section title="Throws (multi-component leads)">
        <p>
          When throws are enabled, you may lead multiple components at once
          (e.g. a pair + a single). The throw <em>fails</em> if any opponent
          holds a higher card in the led suit for any component — in that
          case you're forced to play only your lowest card.
        </p>
      </Section>

      <Section title="Defenders vs. attackers">
        <p>
          The declarer's team are <strong>defenders</strong>; everyone else
          are <strong>attackers</strong>. Only attacker-captured points
          count against the defense.
        </p>
        <table className="rules-table">
          <thead><tr><th>Attacker points</th><th>Result</th><th>Rank change</th></tr></thead>
          <tbody>
            <tr><td>0</td><td>Defenders win big</td><td>Defenders +3</td></tr>
            <tr><td>1 – 35</td><td>Defenders win</td><td>Defenders +2</td></tr>
            <tr><td>36 – 79</td><td>Defenders hold</td><td>Defenders +1</td></tr>
            <tr><td>80 – 119</td><td>Attackers flip</td><td>Attackers +0 (trade sides)</td></tr>
            <tr><td>120 – 159</td><td>Attackers win</td><td>Attackers +1</td></tr>
            <tr><td>160 – 199</td><td>Attackers win big</td><td>Attackers +2</td></tr>
            <tr><td>≥ 200</td><td>Attackers crush</td><td>Attackers +3</td></tr>
          </tbody>
        </table>
        <p className="rules-muted">
          If attackers won the <em>last</em> trick they also collect the
          kitty's points, multiplied by 2 × the largest component of that
          last lead (a pair = ×4, a tractor of two pairs = ×8, etc.).
        </p>
      </Section>

      <Section title="Find-the-Friend mode">
        <p>
          With <strong>5+ players</strong> (or any seat count if the host
          enables it), the declarer secretly calls one or two specific
          cards after burying the kitty. Whoever plays a called copy
          becomes a teammate.
        </p>
        <ul>
          <li>4 players: 1 friend → 2 vs 2</li>
          <li>5 players: 1 friend → 2 vs 3</li>
          <li>6 players: 2 friends → 3 vs 3</li>
        </ul>
        <p>
          Each call specifies <em>rank + suit + ordinal</em> ("the 2nd ♠A
          played"). The card itself can be public (visible to everyone) or
          private (only the seat that plays it is revealed).
        </p>
      </Section>
    </>
  );
}

// ===========================================================================
// Cabo
// ===========================================================================

function CaboRules() {
  return (
    <>
      <Section title="Goal">
        <p>
          Finish the round with the <strong>lowest total score</strong>
          among your face-down cards. Snap matching cards to shrink your
          hand; use power cards to peek &amp; swap.
        </p>
      </Section>

      <Section title="Setup">
        <p>
          2–8 players. Each player is dealt <strong>4, 6, or 8</strong> face-down
          cards (host's choice). You're allowed to peek at your{" "}
          <strong>two bottom cards</strong> at the start of the round —
          everything else is unknown until revealed.
        </p>
        <p className="rules-muted">
          Two jokers are in play. Larger games use a second deck.
        </p>
      </Section>

      <Section title="Card values">
        <table className="rules-table">
          <thead><tr><th>Card</th><th>Points</th></tr></thead>
          <tbody>
            <tr><td>Joker <C id="JK" /></td><td>−1</td></tr>
            <tr><td>Ace <C id="AH" /></td><td>1</td></tr>
            <tr><td>2 – 10 <C id="5D" /> <C id="TS" /></td><td>face value</td></tr>
            <tr><td>Jack <C id="JC" /></td><td>11</td></tr>
            <tr><td>Queen <C id="QD" /></td><td>12</td></tr>
            <tr><td>King <C id="KS" /></td><td>13</td></tr>
          </tbody>
        </table>
      </Section>

      <Section title="Your turn">
        <ol>
          <li>
            <strong>Draw</strong> the top card from the stockpile (or call
            Cabo to start the final countdown — see below).
          </li>
          <li>
            Look at the drawn card privately. Then either:
            <ul>
              <li>
                <strong>Swap</strong> it into one of your slots (or into an
                empty slot for free). The replaced card is revealed to
                everyone on the discard pile.
              </li>
              <li>
                <strong>Discard</strong> it. If it's a 6 – K, its{" "}
                <em>power</em> triggers.
              </li>
            </ul>
          </li>
        </ol>
      </Section>

      <Section title="Power cards">
        <table className="rules-table">
          <thead><tr><th>Card</th><th>Power</th></tr></thead>
          <tbody>
            <tr>
              <td><C id="6H" /> <C id="7C" /></td>
              <td>
                <strong>Peek own</strong> — look at one of your own
                face-down cards. (Memorise it — you won't get to keep
                staring at it.)
              </td>
            </tr>
            <tr>
              <td><C id="8D" /> <C id="9S" /></td>
              <td>
                <strong>Peek other</strong> — look at one of any
                opponent's face-down cards.
              </td>
            </tr>
            <tr>
              <td><C id="TH" /> <C id="JC" /></td>
              <td>
                <strong>Blind swap</strong> — pick any two cards on the
                table (any owners). Swap them without looking. The two
                slots are highlighted so everyone sees which two moved.
              </td>
            </tr>
            <tr>
              <td><C id="QD" /> <C id="KS" /></td>
              <td>
                <strong>Peek &amp; swap</strong> — peek at any two cards on
                the table, then optionally swap them.
              </td>
            </tr>
          </tbody>
        </table>
        <p className="rules-muted">
          After a peek (6/7/8/9), you have a few seconds to register the
          card before tapping <strong>OK</strong> — the server auto-acks
          after 5 seconds.
        </p>
      </Section>

      <Section title="Snap (at any time)">
        <p>
          Any player may click <strong>any face-down card on the table</strong>{" "}
          to try to snap it. The click checks the card's rank against the
          top of the discard pile (suits are ignored).
        </p>
        <ul>
          <li>
            <strong>Match</strong> — the snapped card is removed; the slot
            becomes <em>empty</em> (your other slots keep their positions
            so your memory stays intact).
          </li>
          <li>
            <strong>Match on <em>another</em> player's card</strong> — you
            must then give that player one of <em>your</em> cards (you choose
            which) to slot back in.
          </li>
          <li>
            <strong>Miss</strong> — penalty: you take one extra unknown
            card from the stockpile into your first empty slot (else
            appended).
          </li>
        </ul>
      </Section>

      <Section title="Knocked out at 10+ cards">
        <p>
          As a safety valve against spam snapping, any player whose pile
          exceeds <strong>10 cards</strong> is marked{" "}
          <strong className="rules-out">OUT</strong> for the rest of the
          round. Their turns are skipped; their cards still count at
          scoring.
        </p>
      </Section>

      <Section title="Calling Cabo">
        <p>
          At the start of your turn, instead of drawing, you can{" "}
          <strong>call Cabo</strong>. Once called:
        </p>
        <ul>
          <li>Your cards are <strong>protected</strong> — opponents can't snap, peek, or swap them.</li>
          <li>Every other player gets exactly <strong>one more turn</strong>.</li>
          <li>Then the round ends and all hands are revealed for scoring.</li>
        </ul>
        <p className="rules-muted">
          Call Cabo when you're confident your total is the lowest at the
          table — but if you're wrong, you score normally with no special
          bonus.
        </p>
      </Section>

      <Section title="End of round">
        <p>
          All cards flip face-up; each player's total is the sum of their
          remaining card values. The lowest total wins the round. Blank
          slots are worth zero.
        </p>
      </Section>
    </>
  );
}

// ===========================================================================
// Oh Hell
// ===========================================================================

function OhHellRules() {
  return (
    <>
      <Section title="Goal">
        <p>
          Bid the <strong>exact</strong> number of tricks you'll take this
          hand. Make your bid for a big bonus; miss and you score only the
          tricks you took. The highest cumulative score after the final
          deal wins.
        </p>
      </Section>

      <Section title="Setup">
        <p>
          3–7 players, standard 52-card deck, no jokers. The number of
          cards dealt per player follows a <em>descending then ascending</em>{" "}
          sequence:
        </p>
        <table className="rules-table">
          <thead>
            <tr><th>Players</th><th>Sequence</th><th>Total hands</th></tr>
          </thead>
          <tbody>
            <tr><td>3</td><td>10 → 1 → 10</td><td>19</td></tr>
            <tr><td>4</td><td>10 → 1 → 10</td><td>19</td></tr>
            <tr><td>5</td><td>10 → 1 → 10</td><td>19</td></tr>
            <tr><td>6</td><td>8 → 1 → 8</td><td>15</td></tr>
            <tr><td>7</td><td>7 → 1 → 7</td><td>13</td></tr>
          </tbody>
        </table>
        <p className="rules-muted">
          After dealing, the next card off the deck is turned face-up as
          trump. The dealer rotates each hand.
        </p>
      </Section>

      <Section title="Bidding">
        <p>
          Starting with the player to the dealer's left and going clockwise,
          each player bids a number from 0 to the hand size. The dealer
          bids <strong>last</strong> and is <em>hooked</em>: their bid
          cannot make the total bids equal the number of tricks available.
          (At least one player has to be wrong.)
        </p>
        <p className="rules-muted">
          The UI greys out the forbidden number when it's your turn as
          dealer.
        </p>
      </Section>

      <Section title="Playing">
        <ol>
          <li>The player to the dealer's left leads the first trick.</li>
          <li>You <strong>must follow suit</strong> if you can; otherwise play anything (including a trump).</li>
          <li>Highest trump wins the trick, or — if no one trumped — the highest of the led suit.</li>
          <li>The trick winner leads the next.</li>
        </ol>
      </Section>

      <Section title="Scoring (common)">
        <table className="rules-table">
          <thead>
            <tr><th>Outcome</th><th>Points</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Tricks taken (any)</td>
              <td>+1 per trick</td>
            </tr>
            <tr>
              <td>Bid exactly matched</td>
              <td>+10 bonus</td>
            </tr>
          </tbody>
        </table>
        <p className="rules-muted">
          Example: bid 3, take 3 → 3 + 10 = 13 points. Bid 3, take 4 → 4
          points (no bonus).
        </p>
      </Section>

      <Section title="End of match">
        <p>
          After the final hand of the sequence, the player with the{" "}
          <strong>highest cumulative score</strong> wins.
        </p>
      </Section>
    </>
  );
}

// ===========================================================================
// Euchre
// ===========================================================================

function EuchreRules() {
  return (
    <>
      <Section title="Goal">
        <p>
          A 4-player partnership trick-taking game. Seats 0+2 play against
          seats 1+3. First team to <strong>10 points</strong> wins.
        </p>
      </Section>

      <Section title="Setup">
        <p>
          4 players in fixed partnerships. 24-card deck:{" "}
          <strong>9, 10, J, Q, K, A</strong> in all four suits. Each
          player is dealt 5 cards; the next card off the deck is flipped
          face-up as the proposed trump.
        </p>
        <CardRow ids={["9S", "TS", "JS", "QS", "KS", "AS"]} />
      </Section>

      <Section title="The bowers">
        <p>
          Jacks are special in Euchre:
        </p>
        <ul>
          <li>
            The <strong>right bower</strong> is the Jack of the <em>trump</em> suit
            — the highest trump.
          </li>
          <li>
            The <strong>left bower</strong> is the Jack of the same color as
            trump — the second-highest trump. It counts as trump (not as its
            printed suit) for following suit and trick winners.
          </li>
        </ul>
        <p className="rules-muted">
          Example with <C id="JS" /> as trump: highest cards from top —{" "}
          <C id="JS" /> right bower, <C id="JC" /> left bower,{" "}
          <C id="AS" />, <C id="KS" />, <C id="QS" />, <C id="TS" />,{" "}
          <C id="9S" />. <C id="JC" /> is treated as a spade for the rest
          of the hand.
        </p>
      </Section>

      <Section title="Bidding">
        <p>Two rounds, starting with the player to the dealer's left:</p>
        <ol>
          <li>
            <strong>Round 1</strong> — each player in turn may{" "}
            <strong>order up</strong> the face-up card's suit as trump, or
            pass. If ordered up, the dealer picks up that card into their
            hand and must discard one.
          </li>
          <li>
            <strong>Round 2</strong> — if everyone passed, the up-card is
            turned down. Each player may call any <em>other</em> suit (not
            the turned-down suit) as trump, or pass.{" "}
            <strong>Stick the dealer</strong> is on by default: the dealer
            must call if it gets back to them.
          </li>
        </ol>
        <p>
          The player who names trump is the <strong>maker</strong>. They
          may choose to <strong>go alone</strong> — their partner sits out
          the hand, in exchange for a higher payoff.
        </p>
      </Section>

      <Section title="Playing a trick">
        <ol>
          <li>The player to the dealer's left leads the first trick (or to the dealer's left of the sitting-out partner, if alone).</li>
          <li>
            You must follow the led suit if able.{" "}
            <strong>The left bower counts as trump</strong>, not as its
            printed suit.
          </li>
          <li>
            Highest trump wins, or if no trumps were played, the highest
            of the led suit.
          </li>
        </ol>
      </Section>

      <Section title="Scoring">
        <table className="rules-table">
          <thead>
            <tr><th>Result</th><th>Points</th></tr>
          </thead>
          <tbody>
            <tr><td>Makers take 3 or 4 tricks</td><td>+1 to makers</td></tr>
            <tr><td>Makers take all 5 (march)</td><td>+2 to makers</td></tr>
            <tr><td>Makers <em>alone</em> take all 5</td><td>+4 to makers</td></tr>
            <tr><td>Defenders take 3+ (euchred)</td><td>+2 to defenders</td></tr>
          </tbody>
        </table>
      </Section>

      <Section title="End of match">
        <p>
          First team to reach <strong>10 points</strong> wins the match.
        </p>
      </Section>
    </>
  );
}

// ===========================================================================
// Regicide
// ===========================================================================

function RegicideRules() {
  return (
    <>
      <Section title="Goal">
        <p>
          Win the majority of tricks each hand. Most tricks scores that
          many points (a clean sweep doubles!). First to{" "}
          <strong>50 points</strong> wins.
        </p>
        <p className="rules-muted">Best with 2 players; supports 2–4.</p>
      </Section>

      <Section title="Setup">
        <p>
          Take a standard 52-card deck plus two jokers, and remove all{" "}
          <strong>10s, Jacks, and Queens</strong> (those are score-keeping
          counters in the physical game — we track scores numerically).
          Each player is dealt <strong>9 cards</strong>; the next card is
          flipped face-up as a proposed trump.
        </p>
      </Section>

      <Section title="Rank">
        <p>
          The starting rank order from highest to lowest is:
        </p>
        <p className="rules-muted">
          Joker (high), K, 9, 8, 7, 6, 5, 4, 3, 2, A (low)
        </p>
        <p>
          The rank can be <strong>reversed</strong> mid-round (see below).
          Reversed:
        </p>
        <p className="rules-muted">
          Joker (high), A, 2, 3, 4, 5, 6, 7, 8, 9, K (low)
        </p>
        <p>The joker is permanently the highest card.</p>
      </Section>

      <Section title="Choosing trump">
        <ol>
          <li>
            The <strong>dealer</strong> may take the up-card as their own
            (becomes trump) or pass.
          </li>
          <li>
            If passed, the next player has the same choice, and so on.
          </li>
          <li>
            Whoever picks up adds the card to their hand and must{" "}
            <strong>discard one</strong>.
          </li>
          <li>
            If everyone passes, the dealer chooses the trump suit (no
            card pickup).
          </li>
          <li>
            If a <C id="SJ" /> or <C id="BJ" /> is the up-card, whoever
            picks it up calls any trump suit.
          </li>
        </ol>
      </Section>

      <Section title="Playing a trick">
        <ol>
          <li>The dealer leads the first trick.</li>
          <li>Players must follow the led suit if able.</li>
          <li>
            Highest trump wins; if no trumps were played, highest of the
            led suit. A joker always wins (it has no suit, so when it
            leads no one needs to follow).
          </li>
        </ol>
      </Section>

      <Section title="Changing trump &amp; rank">
        <p>
          When a trick is won by anyone <em>other than</em> the player
          who led it, the winner may:
        </p>
        <ul>
          <li>change the trump suit,</li>
          <li>reverse the rank direction,</li>
          <li>do both,</li>
          <li>or leave both unchanged.</li>
        </ul>
        <p>
          When a trick is won by the player who <em>led</em> it, neither
          can change — they just lead the next trick.
        </p>
      </Section>

      <Section title="Scoring">
        <table className="rules-table">
          <thead><tr><th>Outcome</th><th>Points</th></tr></thead>
          <tbody>
            <tr><td>Most tricks (single winner)</td><td>= trick count</td></tr>
            <tr><td>Sweep — one player wins every trick</td><td>= 2× trick count</td></tr>
            <tr><td>Tie for most tricks</td><td>Each tied player gets their trick count</td></tr>
          </tbody>
        </table>
        <p className="rules-muted">
          Players who didn't tie for the most tricks score 0 that hand.
        </p>
      </Section>

      <Section title="End of match">
        <p>
          First to <strong>50 points</strong> wins. If two players cross
          50 in the same hand and tie at the top, another hand is played.
        </p>
      </Section>
    </>
  );
}

// ===========================================================================
// Swoop
// ===========================================================================

function SwoopRules() {
  return (
    <>
      <Section title="Goal">
        <p>
          Get rid of all your cards. The first player to empty everything
          wins the round; everyone else scores the value of the cards
          they're stuck holding. Match ends when any player reaches{" "}
          <strong>500 points</strong>; the player with the{" "}
          <strong>lowest</strong> cumulative score wins.
        </p>
      </Section>

      <Section title="Setup">
        <p>
          3–8 players. Two to four standard decks are shuffled together
          (with jokers), scaled by player count:
        </p>
        <table className="rules-table">
          <thead>
            <tr><th>Players</th><th>Decks</th><th>Total cards</th></tr>
          </thead>
          <tbody>
            <tr><td>3–4</td><td>2 (with jokers)</td><td>108</td></tr>
            <tr><td>5–6</td><td>3 (with jokers)</td><td>162</td></tr>
            <tr><td>7–8</td><td>4 (with jokers)</td><td>216</td></tr>
          </tbody>
        </table>
        <p>
          Each player gets <strong>19 cards</strong>: 4 face-down{" "}
          <em>mystery</em> cards, 4 face-up cards placed on top of them,
          and 11 in hand. Leftover cards aren't used. Suits are
          irrelevant.
        </p>
      </Section>

      <Section title="Card ranks &amp; values">
        <p>
          Rank order from low to high:{" "}
          <strong>A – 2 – 3 – 4 – 5 – 6 – 7 – 8 – 9 – J – Q – K</strong>.
          10s and Jokers have <strong>no rank</strong> — they're "wild"
          swoop cards (see below).
        </p>
        <table className="rules-table">
          <thead><tr><th>Card</th><th>End-of-round points</th></tr></thead>
          <tbody>
            <tr><td><C id="AH" /></td><td>1</td></tr>
            <tr><td>2 – 9</td><td>face value</td></tr>
            <tr><td>J, Q, K</td><td>10 each</td></tr>
            <tr><td>10s and Jokers</td><td>50 each (ouch)</td></tr>
          </tbody>
        </table>
      </Section>

      <Section title="Playing a turn">
        <ol>
          <li>
            On your turn, play <strong>1 to 4 same-rank cards</strong>{" "}
            onto the central play pile. Cards may come from your hand,
            your face-up table cards, or a mix.
          </li>
          <li>
            If your cards are <strong>≤</strong> the top card of the
            pile, just play them on top.
          </li>
          <li>
            If your cards are <strong>higher</strong> than the top, you
            must first <strong>pick up the entire pile</strong> into your
            hand, then play your cards as a fresh pile.
          </li>
          <li>
            If you don't want to (or can't) play, you can{" "}
            <strong>pick up the pile</strong> instead.
          </li>
          <li>
            Otherwise the turn passes clockwise.
          </li>
        </ol>
      </Section>

      <Section title="Swoops">
        <p>
          A <strong>swoop</strong> sets the entire pile aside (out of
          play) and gives you another turn. Two ways to swoop:
        </p>
        <ul>
          <li>
            <strong>Play a 10 or a Joker</strong> at any time — the pile
            is set aside.
          </li>
          <li>
            Make the <strong>top 4 cards</strong> of the pile all the
            same rank. You can do this by playing four of a kind, or by
            adding to an existing run on top (e.g. 2 8s on top + you play
            2 8s = 4 8s = swoop).
          </li>
        </ul>
        <p className="rules-muted">
          You can't create more than 4 equal cards on top of the pile —
          if there are already 3, you can only add one (which swoops).
          Multiple swoops in a row are possible if you have the cards.
        </p>
      </Section>

      <Section title="Mystery cards">
        <p>
          Your 4 face-down "mystery" cards stay hidden — even from{" "}
          <em>you</em> — until they're flipped over. A mystery card can
          only be played as the <strong>first card</strong> of a turn,
          and only after the face-up card on top of it has been played.
          When you flip one, you must immediately play it as a single
          card. If it's too high, you take the pile and start over with
          it.
        </p>
      </Section>

      <Section title="End of round &amp; match">
        <p>
          As soon as one player has played their last card (hand, table,
          and mystery cards all gone), the round ends. That player wins
          the round and scores <strong>0 points</strong>; every other
          player adds up the value of the cards they're still holding
          and that total is added to their cumulative score.
        </p>
        <p>
          The match ends when any player reaches{" "}
          <strong>500 points</strong>. The player with the{" "}
          <strong>lowest</strong> total wins.
        </p>
      </Section>
    </>
  );
}
